import { BRAND } from '@theme/brand.ts';
import type { ReactNode } from 'react';


import type { CardSummary } from '../api/client.ts';

// The physical card object — the system's signature surface. Isolated here
// so the face can be rendered, themed, and tested independently of the
// dashboard carousel that hosts it.
//
// Variants re-skin the plastic without touching layout: the default face is
// the theme's dark gradient ("noir" under Viora); "champagne" and "ivory"
// override the card-local custom properties in styles.css.
export type CardVariant = 'champagne' | 'ivory';

const isPendingPhysical = (card: CardSummary): boolean =>
  (card.status ?? '').toUpperCase() === 'PENDING' && (card.type ?? '').toUpperCase() === 'PHYSICAL';

export const CardFace = ({
  card,
  noFadeIn = false,
  variant,
}: {
  readonly card: CardSummary;
  readonly noFadeIn?: boolean;
  readonly variant?: CardVariant;
}): ReactNode => {
  const last4 = card.lastFour ?? '••••';
  // The PAN row already shows the last-4, so falling back to `•• ${last4}`
  // would duplicate the number across two adjacent rows. Use a type-derived
  // label instead (e.g. "Virtual" / "Physical"), then "Card" as the last
  // fallback.
  const nickname =
    card.cardLabel ??
    (card.type !== undefined && card.type !== ''
      ? card.type.charAt(0) + card.type.slice(1).toLowerCase()
      : 'Card');
  const dim = isPendingPhysical(card);
  const frozen = (card.status ?? '').toUpperCase() === 'FROZEN';
  const variantCls = variant !== undefined ? ` card-face--${variant}` : '';
  return (
    <div
      className={`card-face${variantCls}${noFadeIn ? '' : ' fade-in d1'}${dim ? ' dim' : ''}${frozen ? ' frozen' : ''}`}
      role="img"
      aria-label={`Card ending ${last4}${frozen ? ', frozen' : ''}`}
    >
      <div className="card-sweep" aria-hidden="true" />
      <div className="card-face-inner">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            {BRAND.cardLogoUrl !== null ? (
              <img className="card-brand-logo" src={BRAND.cardLogoUrl} alt={BRAND.cardLogoAlt} />
            ) : (
              <p className="card-brand-mark">{BRAND.name}</p>
            )}
            <p className="card-brand-tier">{card.type ?? 'Private'}</p>
          </div>
          <div className="card-chip" aria-hidden="true" />
        </div>
        <div>
          <p className="card-pan">•••• •••• •••• {last4}</p>
          <div className="card-bottom">
            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="card-label">Card</p>
              <p className="card-nickname">{nickname}</p>
            </div>
            <p className="card-visa">VISA</p>
          </div>
        </div>
      </div>
    </div>
  );
};
